import { useSearchParams } from 'react-router-dom'
import {
  CCard,
  CCardBody,
  CCol,
  CNav,
  CNavItem,
  CNavLink,
  CRow,
  CTabContent,
  CTabPane,
} from '@coreui/react-pro'
import { role } from 'src/libs'
import { useStore } from 'effector-react'
import { $userRole } from 'src/features/user'
import { ErrorScreen } from 'src/shared/components/errorScreen/errorScreen'
import { AccountsTable } from './components/AccountsTable'
import { CreateAccountForm } from './components/CreateAccountForm'
import { AccountSettingsForm } from './components/AccountSettingsForm'
import { accountsMounted, setAccountsRequestError } from './model'
import { useAccountsWidget } from './hooks'
import { Account } from './types'

export const AccountsWidget = () => {
  const { accounts, loading, errorAccountsRequest } = useAccountsWidget()

  if (![0, 404].includes(errorAccountsRequest.code)) {
    return (
      <ErrorScreen
        showRetry
        onRetry={() => {
          accountsMounted()
          setAccountsRequestError({
            message: '',
            code: 0,
          })
        }}
      />
    )
  }

  return (
    <CCard>
      <CCardBody>
        <AccountsList items={accounts} loading={loading} />
      </CCardBody>
    </CCard>
  )
}

const ACCOUNT_TABS = {
  ACTIVE_REAL: 'activeReal',
  ACTIVE_BONUS: 'activeBonus',
  ACTIVE_DEMO: 'activeDemo',
  ACTIVE_DEMO_CONTEST: 'activeDemoContest',
  ARCHIVE: 'archive',
  CLOSED: 'closed',
  SETTINGS: 'settings',
} as const

type AccountTab = typeof ACCOUNT_TABS[keyof typeof ACCOUNT_TABS]

type AccountsListProps = {
  items: Account[] | undefined
  loading: boolean
}

const AccountsList = ({ items, loading }: AccountsListProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const userRole = useStore($userRole)

  const activeKey = searchParams.get('accountsTab') ?? ACCOUNT_TABS.ACTIVE_REAL

  const setAccountsTab = (tab: AccountTab) => {
    const params = new URLSearchParams(searchParams)

    params.set('tab', 'accounts')
    params.set('accountsTab', tab)

    setSearchParams(params, { replace: true })
  }

  return (
    <div>
      <CNav variant="tabs" role="tablist">
        <CNavItem>
          <CNavLink
            data-tab="activeReal"
            href="#!"
            active={activeKey === ACCOUNT_TABS.ACTIVE_REAL}
            onClick={() => setAccountsTab(ACCOUNT_TABS.ACTIVE_REAL)}
          >
            Active real account
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            data-tab="activeBonus"
            href="#!"
            active={activeKey === ACCOUNT_TABS.ACTIVE_BONUS}
            onClick={() => setAccountsTab(ACCOUNT_TABS.ACTIVE_BONUS)}
          >
            Active bonus account
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            data-tab="activeDemo"
            href="#!"
            active={activeKey === ACCOUNT_TABS.ACTIVE_DEMO}
            onClick={() => setAccountsTab(ACCOUNT_TABS.ACTIVE_DEMO)}
          >
            Active demo account
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            data-tab="activeDemoContest"
            href="#!"
            active={activeKey === ACCOUNT_TABS.ACTIVE_DEMO_CONTEST}
            onClick={() => setAccountsTab(ACCOUNT_TABS.ACTIVE_DEMO_CONTEST)}
          >
            Active demo contest
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            data-tab="archive"
            href="#!"
            active={activeKey === ACCOUNT_TABS.ARCHIVE}
            onClick={() => setAccountsTab(ACCOUNT_TABS.ARCHIVE)}
          >
            Archive account
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            data-tab="closed"
            href="#!"
            active={activeKey === ACCOUNT_TABS.CLOSED}
            onClick={() => setAccountsTab(ACCOUNT_TABS.CLOSED)}
          >
            Closed account
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            data-tab="settings"
            href="#!"
            active={activeKey === ACCOUNT_TABS.SETTINGS}
            onClick={() => setAccountsTab(ACCOUNT_TABS.SETTINGS)}
          >
            Settings
          </CNavLink>
        </CNavItem>
      </CNav>
      <CTabContent>
        <CTabPane
          data-tab="activeReal"
          role="tabpanel"
          aria-labelledby="contact-tab"
          visible={activeKey === ACCOUNT_TABS.ACTIVE_REAL}
        >
          {activeKey === ACCOUNT_TABS.ACTIVE_REAL && (
            <AccountsTable
              items={items?.filter(
                (details) =>
                  details.status == 'active' &&
                  !details.isDemo &&
                  !['bonus111', 'demoContests'].includes(details.tariff),
              )}
              loading={loading}
              type="activeReal"
            />
          )}
        </CTabPane>
        <CTabPane
          data-tab="activeBonus"
          role="tabpanel"
          aria-labelledby="contact-tab"
          visible={activeKey === ACCOUNT_TABS.ACTIVE_BONUS}
        >
          {activeKey === ACCOUNT_TABS.ACTIVE_BONUS && (
            <AccountsTable
              items={items?.filter(
                (details: Account) =>
                  details.status == 'active' && details.tariff == 'bonus111',
              )}
              loading={loading}
              type="activeBonus"
            />
          )}
        </CTabPane>
        <CTabPane
          data-tab="activeDemo"
          role="tabpanel"
          aria-labelledby="contact-tab"
          visible={activeKey === ACCOUNT_TABS.ACTIVE_DEMO}
        >
          {activeKey === ACCOUNT_TABS.ACTIVE_DEMO && (
            <AccountsTable
              items={items?.filter(
                (details) =>
                  details.status == 'active' &&
                  details.isDemo &&
                  details.tariff !== 'demoContests',
              )}
              loading={loading}
              type="activeDemo"
            />
          )}
        </CTabPane>
        <CTabPane
          data-tab="activeDemoContest"
          role="tabpanel"
          aria-labelledby="contact-tab"
          visible={activeKey === ACCOUNT_TABS.ACTIVE_DEMO_CONTEST}
        >
          {activeKey === ACCOUNT_TABS.ACTIVE_DEMO_CONTEST && (
            <AccountsTable
              items={items?.filter(
                (details) =>
                  details.status == 'active' &&
                  details.tariff === 'demoContests',
              )}
              loading={loading}
              type="activeDemoContest"
            />
          )}
        </CTabPane>
        <CTabPane
          data-tab="archive"
          role="tabpanel"
          aria-labelledby="contact-tab"
          visible={activeKey === ACCOUNT_TABS.ARCHIVE}
        >
          {activeKey === ACCOUNT_TABS.ARCHIVE && (
            <AccountsTable
              items={items?.filter((details) => details.status === 'archived')}
              loading={loading}
              type="archive"
            />
          )}
        </CTabPane>
        <CTabPane
          data-tab="closed"
          role="tabpanel"
          aria-labelledby="contact-tab"
          visible={activeKey === ACCOUNT_TABS.CLOSED}
        >
          {/*<AccountsTable*/}
          {/*  items={items?.filter((details) => details.status === 'archived')}*/}
          {/*  loading={loading}*/}
          {/*></AccountsTable>*/}
        </CTabPane>
        <CTabPane
          data-tab="settings"
          role="tabpanel"
          aria-labelledby="contact-tab"
          visible={activeKey === ACCOUNT_TABS.SETTINGS}
        >
          {activeKey === ACCOUNT_TABS.SETTINGS && (
            <CRow>
              <CCol>
                {(userRole?.includes(role.dealer.dealer()) ||
                  userRole?.includes(role.root()) ||
                  userRole?.includes(role.clientSupport.supportManager()) ||
                  userRole?.includes(role.clientSupport.supportAgent()) ||
                  userRole?.includes(role.clientSupport.seniorSupport())) && (
                  <CreateAccountForm />
                )}
              </CCol>
              <CCol>
                {(userRole?.includes(role.dealer.dealer()) ||
                  userRole?.includes(role.root()) ||
                  userRole?.includes(role.clientSupport.supportManager()) ||
                  userRole?.includes(role.clientSupport.supportAgent()) ||
                  userRole?.includes(role.clientSupport.seniorSupport())) && (
                  <AccountSettingsForm />
                )}
              </CCol>
            </CRow>
          )}
        </CTabPane>
      </CTabContent>
    </div>
  )
}
